import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/contexts/AuthContext';
import Layout from './components/layout/Layout';
import './App.css';
import PropTypes from 'prop-types';
import backgroundSyncManager from './utils/BackgroundSyncManager';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const FormSubmit = React.lazy(() => import('./components/formbuilder/FormSubmit'));
const FormBuilder = React.lazy(() => import('./components/formbuilder/formBuilder'));
const FormList = React.lazy(() => import('./pages/FormList'));
const FormResponses = React.lazy(() => import('./components/formbuilder/FormResponses'));
const WarehouseList = React.lazy(() => import('./components/gestionale/WarehouseList'));
const WarehouseBuilder = React.lazy(() => import('./components/gestionale/WarehouseBuilder'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const PaginaAziende = React.lazy(() => import('./pages/PaginaAziende'));
const PaginaRuoli = React.lazy(() => import('./components/roles/PaginaRuoli'));
const DynamicProductForm = React.lazy(() => import('./components/gestionale/DynamicProductForm'));
const PublicFormPage = React.lazy(() => import('./components/formbuilder/PublicForm'));
const ProductListByCategory = React.lazy(() => import('./components/gestionale/ProductListByCategory'));
const CarStockList = React.lazy(() => import('./components/carstock/StockPage'));
const ClientManagement = React.lazy(() => import('./components/crm/ClientManagement'));
const ClientDetailsPage = React.lazy(() => import('./components/crm/ClientDetailsPage'));
const CustomerSegmentsPage = React.lazy(() => import('./components/crm/ClientSegmentPage'));
const ClientImport = React.lazy(() => import('./components/crm/ClientImport'));
const ClientApiKeys = React.lazy(() => import('./components/crm/ClientApiKeys'));
const OpportunityStagesPage = React.lazy(() => import('./components/crm/OpportunityStagesPage'));
const QualificationFieldsBuilder = React.lazy(() => import('./components/crm/QualificationFieldsBuilder'));
const CalendarPage = React.lazy(() => import('./components/booking/CalendarPage'));
const AvailabilityManagementPage = React.lazy(() => import('./components/booking/settings/AvaibilityManagementPage'));
const CarEdit = React.lazy(() => import('./components/carstock/CarEdit'));
const ChatsPage = React.lazy(() => import('./components/chats/ChatPage'));
const TrainingMessages = React.lazy(() => import('./components/training/TrainingMessages'));
const UserManagement = React.lazy(() => import('./components/users/UserManagement'));
const OpenChat = React.lazy(() => import('./components/openchat/Openchat'));
const InstallGuide = React.lazy(() => import('./components/pwa/InstallGuide'));
const ClientChat = React.lazy(() => import('./components/crm/ClientChat'));
const BucketManagement = React.lazy(() => import('./components/documents/BucketManagement'));
const DocumentsTableView = React.lazy(() => import('./components/documents/DocumentsTableView'));
const DocumentsToolTest = React.lazy(() => import('./components/documents/DocumentsToolTest'));
const SeoManager = React.lazy(() => import('./components/carstock/SeoManager'));
const PromptRulesManagement = React.lazy(() => import('./components/documents/PromptRulesManagement'));
const MonitoringDashboard = React.lazy(() => import('./components/monitoring/MonitoringDashboard'));
const EmailTemplateManagementPage = React.lazy(() => import('./components/booking/settings/EmailTemplateManagementPage'));
const VoiceAgentSettingsPage = React.lazy(() => import('./components/voice_agent/VoiceAgentSettingsPage'));
const OutboundPage = React.lazy(() => import('./components/outbound/OutboundPage'));
const AutoscoutMarket = React.lazy(() => import('./components/carstock/AutoscoutMarket'));
const CatalogPage = React.lazy(() => import('./components/carstock/CatalogPage'));
const CatalogTrimDetailPage = React.lazy(() => import('./components/carstock/CatalogTrimDetailPage'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <img src="/AitomotiveLab.svg" alt="Logo" style={{ width: '150px', marginBottom: '20px' }} />
    <CircularProgress size={32} />
  </Box>
);

const theme = createTheme({
  typography: {
   "fontFamily": `"Poppins", sans-serif`,
  },
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#115293',
    },
    secondary: {
      main: '#f50057',
    },
  }
});

const ProtectedRoute = ({ children, requiredApp, altApp, superAdminOnly, allowSelfService }) => {
  const { user, loading, appAccess, userRole } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh'
      }}>
        <img 
          src="/AitomotiveLab.svg" 
          alt="Logo"
          style={{
            width: '150px',
            marginBottom: '20px'
          }}
        />
        <div>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if route is superadmin-only and user is not superadmin
  if (superAdminOnly && userRole !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Bypass permission checks for superadmin
  if (userRole === 'superadmin') {
    // Give superadmin full permissions
    const fullPermissions = {
      can_view: true,
      can_edit: true,
      can_delete: true,
      can_create: true
    };

    // Clone the child element and pass fullPermissions as props
    const childrenWithProps = React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { permissions: fullPermissions });
      }
      return child;
    });

    return <Layout>{childrenWithProps}</Layout>;
  }

  // If not superadmin, apply normal permission checks
  let permissions = {
    can_view: true,
    can_edit: true,
    can_delete: true,
    can_create: true
  };

  if (requiredApp && requiredApp !== 'Required') {
    const appPermissions = appAccess && appAccess[requiredApp];
    // Check alternative app permission (e.g., settings OR ai_schedule)
    const altAppPermissions = altApp && appAccess && appAccess[altApp];
    
    const hasPrimaryAccess = appPermissions && appPermissions.can_view;
    const hasAltAccess = altAppPermissions && altAppPermissions.can_view;
    
    // If allowSelfService is true, don't redirect - let the component handle it
    // This allows users to access their own profile even without can_view permission
    if (!allowSelfService && !hasPrimaryAccess && !hasAltAccess) {
      // Redirect to dashboard if user doesn't have view permission for either app
      return <Navigate to="/dashboard" replace />;
    }
    
    // Use primary app permissions if available, otherwise use alt app permissions
    const effectivePermissions = hasPrimaryAccess ? appPermissions : altAppPermissions;
    
    permissions = {
      can_view: effectivePermissions?.can_view || false,
      can_edit: effectivePermissions?.can_edit || false,
      can_delete: effectivePermissions?.can_delete || false,
      can_create: effectivePermissions?.can_create || false
    };
  }

  // Clone the child element and pass permissions as props for non-superadmin users
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { permissions });
    }
    return child;
  });

  return <Layout>{childrenWithProps}</Layout>;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredApp: PropTypes.string,
  altApp: PropTypes.string,
  superAdminOnly: PropTypes.bool,
  allowSelfService: PropTypes.bool,
};

const App = () => {
  // Global BackgroundSyncManager registration - ensures sync works even when not on Chats page
  useEffect(() => {
    // Register a global sync callback that triggers Service Worker sync
    // This ensures background sync happens regardless of which page the user is on
    const unsubscribe = backgroundSyncManager.registerSyncCallback(
      'app-global-sync',
      async ({ timeInBackground, reason, isLongBackground }) => {
        console.log(`[App] 🔄 Global BackgroundSyncManager callback (reason: ${reason}, background: ${Math.round(timeInBackground/1000)}s)`);
        
        // Only trigger SW sync if we've been in background for a bit
        if (timeInBackground < 1000 && reason === 'visibility_change') {
          console.log('[App] Short background time, skipping SW sync trigger');
          return;
        }
        
        // Trigger Service Worker sync for chat data pre-fetch
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            if (registration?.active) {
              console.log('[App] 📤 Triggering SW sync from global callback');
              registration.active.postMessage({
                type: 'TRIGGER_SYNC',
                reason: reason || 'visibility_change',
                force: isLongBackground
              });
            }
          } catch (err) {
            console.warn('[App] Failed to trigger SW sync:', err);
          }
        }
      },
      { priority: 1, minBackgroundTime: 0 } // High priority (runs first), always runs
    );
    
    return () => {
      unsubscribe();
    };
  }, []);
  
  // Global frontend handling for push notifications coming from the Service Worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    function onSwMessage(event) {
      const data = event?.data || {};
      
      // Handle immediate navigation from notification click
      if (data?.type === 'NOTIFICATION_NAVIGATE') {
        const { url, chatId } = data;
        console.log('[App] 🔔 NOTIFICATION_NAVIGATE received:', { url, chatId });
        
        // Dispatch event for immediate chat navigation (handled by Chats.jsx)
        if (chatId) {
          window.dispatchEvent(new CustomEvent('notification-navigate-chat', {
            detail: { chatId, url, timestamp: Date.now() }
          }));
        }
        
        // If URL differs from current, navigate
        if (url && window.location.href !== url) {
          const currentPath = window.location.pathname;
          const targetPath = new URL(url, window.location.origin).pathname;
          if (currentPath !== targetPath) {
            window.location.href = url;
          }
        }
        return;
      }
      
      if (data?.type !== 'PUSH_MESSAGE') return;
      
      const payload = data?.data || {};
      const notificationId = payload.notification_id || payload.notificationId;
      const recipientUserId = payload.recipient_user_id || null;
      const backendBase = (payload.backend_url || payload.backendUrl || (process.env.REACT_APP_DOMAIN || ''))?.replace(/\/$/, '');
      const chatId = payload.chat_id || null;
      const app = payload.app || null;
      
      // Emit a custom event so components (like Chats) can react to push notifications
      // This allows refreshing the chat list when a new_chat or new_message notification arrives
      try {
        window.dispatchEvent(new CustomEvent('push-notification-received', {
          detail: {
            title: data?.title || '',
            body: data?.body || '',
            url: data?.url || '',
            chatId,
            app,
            notificationId,
            payload
          }
        }));
        console.log('[App] Dispatched push-notification-received event:', { chatId, app, title: data?.title });
      } catch (e) {
        console.warn('[App] Failed to dispatch push-notification-received event:', e);
      }
      
      // Click tracking logic (existing)
      const shouldArmClick = !!(payload.frontend_click_on_user_action || payload.frontendClickOnUserAction);
      if (!shouldArmClick || !notificationId || !backendBase) return;

      const handler = async () => {
        try {
          await fetch(`${backendBase}/notifications/api/track-click/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({ notification_id: notificationId, action: 'open', tag: data?.tag || 'frontend', chat_id: chatId, recipient_user_id: recipientUserId })
          });
        } catch (_) { /* ignore */ }
      };

      // Arm a one-time click handler on document
      const onceHandler = () => {
        document.removeEventListener('click', onceHandler, true);
        handler();
      };
      document.addEventListener('click', onceHandler, true);
    }

    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>
        <AuthProvider>
          <Router>
            <Suspense fallback={<LazyFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/openchat/:chatId" element={<OpenChat />} />
              <Route path="/chat/:chatId" element={<OpenChat />} /> // Changed from /openchat/:chatId
              <Route path="/publicform/:formId" element={<PublicFormPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
              <Route path="/forms/submit/:formId" element={<FormSubmit />} />

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute requiredApp="ai_app">
                  <MonitoringDashboard />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute requiredApp="ai_app">
                  <MonitoringDashboard />
                </ProtectedRoute>
              } />
              <Route path="/car_stock" element={
                <ProtectedRoute requiredApp="car_stock">
                  <CarStockList />
                </ProtectedRoute>
              } />
              <Route path="/cars/:id/edit" element={
                <ProtectedRoute requiredApp="car_stock">
                  <CarEdit />
                </ProtectedRoute>
              } />
              <Route path="/seo-manager" element={
                <ProtectedRoute requiredApp="car_stock">
                  <SeoManager />
                </ProtectedRoute>
              } />
              <Route path="/autoscout-market" element={
                <ProtectedRoute requiredApp="autoscout_market">
                  <AutoscoutMarket />
                </ProtectedRoute>
              } />
              <Route path="/configuratore" element={
                <ProtectedRoute requiredApp="car_stock">
                  <CatalogPage />
                </ProtectedRoute>
              } />
              <Route path="/configuratore/trim/:providerCode" element={
                <ProtectedRoute requiredApp="car_stock">
                  <CatalogTrimDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute requiredApp="crm_app">
                  <ClientManagement />
                </ProtectedRoute>
              } />
              <Route path="/clients/import" element={
                <ProtectedRoute requiredApp="crm_app">
                  <ClientImport />
                </ProtectedRoute>
              } />
              <Route path="/clients/api-keys" element={
                <ProtectedRoute requiredApp="crm_app">
                  <ClientApiKeys />
                </ProtectedRoute>
              } />
              <Route path="chats/:chatId?" element={
                <ProtectedRoute requiredApp="ai_app">
                  <ChatsPage />
                </ProtectedRoute>
              } />
              <Route path="/training-messages" element={
                <ProtectedRoute superAdminOnly={true}>
                  <TrainingMessages />
                </ProtectedRoute>
              } />
              <Route path="/clients/:id" element={
                <ProtectedRoute requiredApp="crm_app">
                  <ClientDetailsPage />
                </ProtectedRoute>
              } />
              <Route path="/clients/segments" element={
                <ProtectedRoute requiredApp="crm_app">
                  <CustomerSegmentsPage />
                </ProtectedRoute>
              } />
              <Route path="/clients/stages" element={
                <ProtectedRoute requiredApp="crm_app">
                  <OpportunityStagesPage />
                </ProtectedRoute>
              } />
              <Route path="/clients/qualification-fields" element={
                <ProtectedRoute requiredApp="crm_app">
                  <QualificationFieldsBuilder />
                </ProtectedRoute>
              } />
              <Route path="/booking" element={
                <ProtectedRoute requiredApp="booking">
                  <CalendarPage />
                </ProtectedRoute>
              } />
              <Route path="/booking/availability" element={
                <ProtectedRoute requiredApp="booking">
                  <AvailabilityManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/warehouse" element={
                <ProtectedRoute requiredApp="Required">
                  <WarehouseList />
                </ProtectedRoute>
              } />
              <Route path="/warehouses/new" element={
                <ProtectedRoute requiredApp="Required">
                  <WarehouseBuilder />
                </ProtectedRoute>
              } />
              <Route path="/warehouses/:warehouseId/edit" element={
                <ProtectedRoute requiredApp="Required">
                  <WarehouseBuilder />
                </ProtectedRoute>
              } />
              <Route path="/warehouses/:warehouseId/products" element={
                <ProtectedRoute requiredApp="Required">
                  <DynamicProductForm />
                </ProtectedRoute>
              } />
              <Route path="/warehouses/:warehouseId/products-by-category" element={
                <ProtectedRoute requiredApp="Required">
                  <ProductListByCategory />
                </ProtectedRoute>
              } />
              <Route path="/utenti" element={
                <ProtectedRoute requiredApp="user_management" allowSelfService={true}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/aziende" element={
                <ProtectedRoute requiredApp="settings" altApp="ai_schedule">
                  <PaginaAziende />
                </ProtectedRoute>
              } />
              <Route path="/ruoli" element={
                <ProtectedRoute requiredApp="user_management">
                  <PaginaRuoli />
                </ProtectedRoute>
              } />
              <Route path="/forms" element={
                <ProtectedRoute requiredApp="forms">
                  <FormList />
                </ProtectedRoute>
              } />
              <Route path="/forms/new" element={
                <ProtectedRoute requiredApp="forms">
                  <FormBuilder />
                </ProtectedRoute>
              } />
              <Route path="/forms/:id/edit" element={
                <ProtectedRoute requiredApp="forms">
                  <FormBuilder />
                </ProtectedRoute>
              } />
              <Route path="/forms/:formId/responses" element={
                <ProtectedRoute requiredApp="forms">
                  <FormResponses />
                </ProtectedRoute>
              } />
              <Route path="/install-guide" element={<InstallGuide />} />
              <Route path="/documents" element={
                <ProtectedRoute requiredApp="documents">
                  <DocumentsTableView />
                </ProtectedRoute>
              } />
              <Route path="/documents-old" element={
                <ProtectedRoute requiredApp="documents">
                  <BucketManagement />
                </ProtectedRoute>
              } />
              <Route path="/documents/tool-test" element={
                <ProtectedRoute requiredApp="documents">
                  <DocumentsToolTest />
                </ProtectedRoute>
              } />
              <Route path="/prompt-rules" element={
                <ProtectedRoute requiredApp="documents">
                  <PromptRulesManagement />
                </ProtectedRoute>
              } />
              <Route path="/message-templates" element={
                <ProtectedRoute requiredApp="settings">
                  <EmailTemplateManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/voice-agent" element={
                <ProtectedRoute requiredApp="voice_agent">
                  <VoiceAgentSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/outbound" element={
                <ProtectedRoute requiredApp="outbound_app">
                  <OutboundPage />
                </ProtectedRoute>
              } />
              <Route path="/old-dashboard" element={
                <ProtectedRoute requiredApp="Required">
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/client-chat/:chatId" element={<ClientChat />} />
            </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </CssBaseline>
    </ThemeProvider>
  );
};

export default App;
